import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react'
import { Loading } from './Loading'

interface LoadingContextValue {
  isLoading: boolean
  /** Muestra un overlay global de carga con un mensaje opcional. */
  begin: (label?: string) => void
  /** Oculta el overlay global. */
  end: () => void
}

const LoadingContext = createContext<LoadingContextValue | null>(null)

/**
 * Sistema de carga global del shell: overlay controlado por contexto.
 * (El Suspense de rutas usa el mismo componente <Loading/> como fallback.)
 */
export function LoadingProvider({ children }: { children: ReactNode }) {
  const [label, setLabel] = useState<string | null>(null)

  const begin = useCallback((next?: string) => setLabel(next ?? 'Procesando…'), [])
  const end = useCallback(() => setLabel(null), [])

  const value = useMemo<LoadingContextValue>(
    () => ({ isLoading: label !== null, begin, end }),
    [label, begin, end],
  )

  return (
    <LoadingContext.Provider value={value}>
      {children}
      {label !== null && (
        <div className="loading-overlay" role="alertdialog" aria-modal="true" aria-label={label}>
          <div className="loading-overlay__panel">
            <Loading label={label} />
          </div>
        </div>
      )}
    </LoadingContext.Provider>
  )
}

export function useLoading(): LoadingContextValue {
  const ctx = useContext(LoadingContext)
  if (!ctx) {
    throw new Error('useLoading must be used within <LoadingProvider>')
  }
  return ctx
}
